import classNames from 'classnames';
import Slider from 'rc-slider';
import { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import styles from './range-slider.module.scss';

export interface RangeSliderProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the slider is disabled */
  disabled?: boolean;
  /** Indicates whether to display the current value of the slider */
  displayValue?: boolean;
  /** A function to format the displayed value */
  formatValue?: (value: number) => string;
  /** The maximum label */
  maxLabel?: string;
  /** The maximum value for the slider */
  maxValue?: number;
  /** The minimum label */
  minLabel?: string;
  /** The minimum value for the slider */
  minValue?: number;
  /** An event that is triggered after the drag has finished */
  onChange: (value: number) => void;
  /** Whether the value can be updated externally */
  shouldUpdateValue?: boolean;
  /** The amount the slider moves when dragging */
  step?: number;
  /** Unit for the slider value, displayed to the right of the value */
  unit?: string;
  /** The current value of the slider */
  value?: number;
  /** Additional label to be displayed next to the value on top of the slider */
  valueLabel?: string;
}

const RangeSlider = ({
  className,
  disabled = false,
  displayValue = false,
  formatValue,
  maxLabel,
  maxValue,
  minLabel,
  minValue = 0,
  onChange,
  shouldUpdateValue = false,
  step = 1,
  unit,
  value: initialValue = minValue,
  valueLabel,
}: RangeSliderProps) => {
  const [currentValue, setCurrentValue] = useState(initialValue);

  const handleChange = (newValue: number) => {
    setCurrentValue(newValue);
  };

  const handleChangeComplete = (newValue: number) => {
    onChange(newValue);
  };

  const formattedValue = formatValue
    ? formatValue(currentValue)
    : unit
      ? `${currentValue} ${unit}`
      : `${currentValue}`;

  useEffect(() => {
    if (!shouldUpdateValue) return;

    setCurrentValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <div
      className={classNames(
        styles.component,
        {
          [styles.isDisabled]: disabled,
        },
        className,
      )}
    >
      {displayValue && (
        <div className={styles.topLabel}>
          <Typography tagStyle="bodyMedium">
            {valueLabel} {formattedValue}
          </Typography>
        </div>
      )}

      <Slider
        disabled={disabled}
        max={maxValue}
        min={minValue}
        onChange={value => handleChange(value as number)}
        onChangeComplete={value => handleChangeComplete(value as number)}
        step={step}
        value={currentValue}
      />

      <div className={styles.bottomLabels}>
        <Typography tagStyle="bodyMedium">{minLabel}</Typography>
        <Typography tagStyle="bodyMedium">{maxLabel}</Typography>
      </div>
    </div>
  );
};

RangeSlider.displayName = 'RangeSlider';

export default RangeSlider;
